import axios from 'axios';
import { BulkDownloadRequest, ApiErrorResponse } from './types/types';
import { extractFilename, downloadFromUrl } from './downloadUtils';

const BASE_URL = process.env.REACT_APP_API_URL || '/api';

const handleImmediateDownload = (response: any): void => {
  const blob = new Blob([response.data], { type: 'application/zip' });
  const contentDisposition = response.headers?.['content-disposition'];
  const filename = extractFilename(contentDisposition) || 'bulk_download.zip';

  const url = URL.createObjectURL(blob);
  downloadFromUrl(url, filename);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};


const handleApiError = (error: any): Error => {
  if (error.response?.data) {
    let errorData: ApiErrorResponse;

    // Handle arraybuffer error responses
    if (error.response.data instanceof ArrayBuffer) {
      try {
        const text = new TextDecoder().decode(error.response.data);
        errorData = JSON.parse(text);
      } catch {
        errorData = { error: 'Invalid response format' };
      }
    } else {
      errorData = error.response.data;
    }

    const { status } = error.response;

    switch (status) {
      case 400:
        if (errorData.error === 'File size limit exceeded') {
          return new Error('Some files are too large for download. Please contact support.');
        }
        return new Error(errorData.error || 'Invalid request');

      case 403:
        if (errorData.details?.inaccessible_files) {
          const count = errorData.details.inaccessible_files.length;
          return new Error(`Access denied to ${count} file(s). You may not have permission to download some selected batches.`);
        }
        return new Error('You don\'t have permission to download these batches');

      case 429: {
        const retryAfter = errorData.retry_after || 120;
        return new Error(`Maximum downloads exceeded. Please wait ${Math.round(retryAfter / 60)} minutes before trying again.`);
      }

      default:
        return new Error(errorData.error || errorData.message || 'Download request failed');
    }
  }

  if (error.code === 'ECONNABORTED') {
    return new Error('Download request timed out. Please try again');
  }

  return new Error(error.message || 'An unexpected error occurred during download');
};

export const initiateDownload = async (fileIds: number[], options?: { zipName?: string }): Promise<void> => {
  const request: BulkDownloadRequest = {
    file_ids: fileIds,
    options: {
      zip_name: options?.zipName || 'selected_files'
    }
  };

  try {
    const response = await axios.post(
      `${BASE_URL}/advance-batches/bulk-download/`,
      request,
      {
        headers: {
          'Content-Type': 'application/json',
        },
        timeout: 120000,
        responseType: 'arraybuffer'
      }
    );

    handleImmediateDownload(response);

  } catch (error) {
    console.error('Bulk download request error:', error);
    throw handleApiError(error);
  }
};